/**
 * 75:2's 'Assuntos que aparecem' card: each Tema/Pauta value's post count and its share of the
 * top one, its children nested beneath it, over
 * `api`'s `reading/topics` route (`AccountTopicsOut`). Reuses `RankRow` (`chrome/chart`), the
 * existing label/value/share bar -- no hand-rolled bar for this shape.
 */

import type { ReactNode } from 'react';

import { RankRow } from '../../../../chrome/chart/RankRow';
import { EmptyState } from '../../../../chrome/state/EmptyState';
import { QueryStates } from '../../../../chrome/state/QueryStates';
import { getLocale } from '../../../../i18n';
import { integer } from '../../../../i18n/format';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Skeleton } from '../../../../ui/skeleton';
import { useAccountTopics, type ReadingWindow } from '../api/queries';
import type { AccountTopics as AccountTopicsData } from '../api/schemas';
import { am, amPlural } from '../messages';

function isEmptyTopics(data: AccountTopicsData): boolean {
  return data.topics.length === 0;
}

function TopicsBody(): ReactNode {
  return (
    <div aria-hidden className="space-y-3">
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="space-y-1.5">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-track w-full rounded-pill" />
        </div>
      ))}
    </div>
  );
}

export function TopicsSkeleton(): ReactNode {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-56" />
      </CardHeader>
      <CardContent>
        <TopicsBody />
      </CardContent>
    </Card>
  );
}

function TopicRows(props: { data: AccountTopicsData }): ReactNode {
  const locale = getLocale();
  const { topics } = props.data;
  const max = Math.max(...topics.map((topic) => topic.count));

  return (
    <div className="flex flex-col gap-3">
      {topics.map((topic) => (
        <div key={topic.theme} className="flex flex-col gap-2">
          <RankRow
            label={topic.theme}
            value={integer(topic.count, locale)}
            share={max > 0 ? topic.count / max : 0}
          />
          {topic.children.length > 0 && (
            <div className="flex flex-col gap-2 border-l border-border pl-4">
              {topic.children.map((child) => (
                <RankRow
                  key={child.theme}
                  label={child.theme}
                  value={integer(child.count, locale)}
                  share={max > 0 ? child.count / max : 0}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function Topics(props: {
  campaignId: string;
  trackedAccountId: string;
  window: ReadingWindow;
}): ReactNode {
  const query = useAccountTopics(props.campaignId, props.trackedAccountId, props.window);
  const relatedPosts = query.data?.related_posts ?? null;
  const subtitle =
    relatedPosts !== null
      ? amPlural('topics.subtitle.other', relatedPosts, { n: relatedPosts })
      : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{am('topics.title')}</CardTitle>
        {subtitle !== undefined && <p className="text-body-sm text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <QueryStates
          query={query}
          isEmpty={isEmptyTopics}
          loading={<TopicsBody />}
          empty={
            <EmptyState
              title={am('topics.empty.title')}
              description={am('topics.empty.description')}
            />
          }
        >
          {(data) => <TopicRows data={data} />}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
